import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { FOCUS_RING, FOCUS_RING_INSET } from './focusRing.ts'

/**
 * The avatar, name and dropdown in the top bar's right corner.
 *
 * Ported from V1. Renders nothing without a session -- which is what keeps the
 * login screen showing the lockup alone, exactly as V1 does.
 *
 * The chevron is inline SVG rather than an icon-font glyph because it rotates,
 * and V1 wanted the rotation to be the same 120ms everywhere it appears.
 *
 * **The dropdown is portalled to `document.body`.** It kept opening behind the
 * queue's sticky table header, and raising its z-index, then isolating the
 * table's stacking context, each looked correct and neither fixed it. The
 * fault was the approach: an overlay that has to float above arbitrary page
 * content should not depend on the stacking context it happens to be declared
 * inside, because any ancestor can create one -- a transform, a filter, an
 * `isolate`, a positioned parent -- and every such change silently re-breaks
 * it somewhere else. Rendered on `body` it has no ancestor to lose to.
 *
 * The cost is positioning by measurement rather than by `absolute`, and
 * re-measuring while it is open, which is what the listeners below are for.
 */
export function UserMenu({
  name,
  children,
}: {
  /** Absent when there is no session. */
  name: string | undefined
  /** Menu items. The caller owns what a session can do. */
  children?: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [at, setAt] = useState<{ top: number; right: number } | undefined>()

  /** Where the menu sits, in viewport coordinates, under the trigger. */
  const place = useCallback(() => {
    const box = triggerRef.current?.getBoundingClientRect()
    if (box === undefined) return
    setAt({ top: box.bottom + 8, right: Math.max(8, window.innerWidth - box.right) })
  }, [])

  useEffect(() => {
    if (!open) return undefined
    place()
    // `true` so a scroll inside any container counts, not just the window's.
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, place])

  // Close on outside click or Escape. Both, because a dropdown that only
  // closes on click traps someone navigating by keyboard.
  useEffect(() => {
    if (!open) return
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node
      // The menu is no longer inside the container, so both are asked --
      // otherwise clicking an item would count as an outside click.
      const inside =
        containerRef.current?.contains(target) === true ||
        menuRef.current?.contains(target) === true
      if (!inside) setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  if (name === undefined || name === '') return null

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="User menu"
        className={`rounded-base flex cursor-pointer items-center gap-3 select-none ${FOCUS_RING}`}
      >
        <div
          className="text-text-strong bg-fill-brand-weak flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold"
          aria-hidden="true"
        >
          {userInitials(name)}
        </div>
        <span className="text-text-strong text-sm">{name}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
          className={`text-text-weak transition-transform duration-[120ms]${open ? ' rotate-180' : ''}`}
        >
          <path
            d="M4 6l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open &&
        at !== undefined &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label="User menu options"
            className="bg-background-raised rounded-base shadow-raised fixed z-50 min-w-[180px] overflow-hidden border border-[rgba(0,9,51,0.08)]"
            style={{ top: at.top, right: at.right }}
            onClick={() => setOpen(false)}
          >
            {children}
          </div>,
          document.body,
        )}
    </div>
  )
}

/**
 * One row in the dropdown. Inset focus ring: the item meets the menu's edge,
 * and an outside ring would be clipped by the menu's `overflow-hidden`.
 */
export function UserMenuItem({
  onClick,
  children,
  indented = false,
  tone = 'neutral',
  icon,
}: {
  onClick: () => void
  children: ReactNode
  /** Under a group heading: a little in, so the heading reads as the parent. */
  indented?: boolean
  /** Leaving, or something else that should read as such. */
  tone?: 'neutral' | 'danger'
  icon?: IconDefinition
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`hover:bg-background-sunken flex w-full cursor-pointer items-center gap-2 py-2.5 pr-4 text-left text-sm transition-colors ${
        tone === 'danger' ? 'text-text-error' : 'text-text-weak'
      } ${indented ? 'pl-7' : 'pl-4'} ${FOCUS_RING_INSET}`}
    >
      {icon !== undefined && (
        <FontAwesomeIcon
          icon={icon}
          aria-hidden="true"
          className={`text-xs ${tone === 'danger' ? 'text-icon-error' : 'text-icon-neutral'}`}
        />
      )}
      {children}
    </button>
  )
}

/**
 * Initials from a display name. An email address gets the part before the `@`,
 * because "e" reads better than nothing when someone has no name set.
 */
function userInitials(name: string): string {
  const source = name.includes('@') ? (name.split('@')[0] ?? name) : name
  const words = source.split(/[\s._-]+/).filter((w) => w !== '')
  const first = words[0]?.[0] ?? ''
  const second = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : ''
  return (first + second).toUpperCase()
}

/**
 * A heading over a run of items: what the items below it are about.
 *
 * Operators get a dozen entries; without headings that reads as a list of
 * words. Not a menuitem, so keyboard traversal skips it.
 */
export function UserMenuGroup({ label }: { label: string }) {
  return (
    <div
      aria-hidden="true"
      className="text-text-weak border-stroke-weak mt-1 border-t px-4 pt-2.5 pb-1 text-[11px] font-semibold tracking-widest uppercase first:mt-0 first:border-t-0"
    >
      {label}
    </div>
  )
}
